import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {Response} from "@QUERIES/types";
import {UseQueryOptions, useQuery} from "@tanstack/react-query";

interface GoodsRecentEvent {
	evCd: string;
	evStartDt: string;
}

type SearchType = "amt" | "day";

const key = "useGoodsRecentEvent";
/** ```
 * 상품 예약이 가능한 첫번째 행사코드
 *   `/pkg/goods/{goodsCd}/recent-event/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const getPath = (goodsCd: string, searchType: SearchType, startDate: string): string => {
	if (!goodsCd) return null;
	return `${publicRuntimeConfig.ENV_PORTAL_API_URL}/pkg/goods/${goodsCd}/recent-event/${searchType}${
		startDate ? `?startDate=${startDate}` : ``
	}`;
};
/** ```
 *  해당 상품의 가장 빠른 행사 , 일자 , 전시된 월
 *   `/pkg/goods/{goodsCd}/recent-event/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const goodsRecentEventAsync = (goodsCd: string, searchType: SearchType = "amt", startDate = "") =>
	useConfigureAxios(getPath(goodsCd, searchType, startDate), {
		method: "GET",
	});

/** ```
 *  해당 상품의 가장 빠른 행사 , 일자 , 전시된 월
 *   `/pkg/goods/{goodsCd}/recent-event/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const goodsRecentEventSSR = async (goodsCd: string, searchType: SearchType = "amt", startDate = "") =>
	useConfigureAxios(getPath(goodsCd, searchType, startDate), {
		method: "GET",
	});

/** ```
 * 상품 예약이 가능한 첫번째 행사코드
 *   `/goods/{goodsCd}/recent-event/{searchType}`
 * parameter
 *   `goodsCd` 상품코드
 *   `searchType` 검색타입 amt | day
 * ```*/
const useGoodsRecentEvent = (
	goodsCd: string,
	searchType: SearchType = "amt",
	startDate = "",
	options?: Partial<UseQueryOptions<Response<GoodsRecentEvent>, Error>>,
) =>
	useQuery({
		queryKey: [key, getPath(goodsCd, searchType, startDate)],
		queryFn: () => useConfigureAxios({url: getPath(goodsCd, searchType, startDate)}).then(r => r.data),
		staleTime: 1000 * 30 * 60,
		...options,
	});

export default useGoodsRecentEvent;
export {getPath, goodsRecentEventSSR, goodsRecentEventAsync};
export type {GoodsRecentEvent};
